import {
  Cancel as CancelIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ApplicationButton, LocalizedTextField } from '../../../../MicrospecDynamicUI/Components';
import { useOrganizationContext } from '../../../../MicrospecDynamicUI/Dashboard/OrganizationContext';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { getDocById, saveDocument } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import { BusinessSpecificationCategory } from '../../../../MicrospecDynamicUI/Models/Business/BusinessSpecificationCategory';
import { useMicrospecThemeLanguageContext } from '../../../../MicrospecDynamicUI/Themes';
import { SPECIFICATION_CATEGORIES_COLLECTION } from '../../../constants/collectionPaths';

interface UpsertSpecificationCategoryProps {
  specCategoryId?: string;
  onSave?: (item: BusinessSpecificationCategory) => void;
  onCancel?: () => void;
}

const UpsertSpecificationCategory: React.FC<UpsertSpecificationCategoryProps> = ({ specCategoryId, onSave, onCancel }) => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const { translate } = localizationProvider;
  const theme = useTheme();
  const { usedLocalization } = useOrganizationContext();
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;
  const db = firestore?.instance;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<BusinessSpecificationCategory>({
    name: usedLocalization?.supported?.length > 1 ? {} : '',
    description: usedLocalization?.supported?.length > 1 ? {} : '',
  });

  useEffect(() => {
    const load = async () => {
      if (!specCategoryId || !db) return;
      setLoading(true);
      try {
        const data = await getDocById(null, null, db, SPECIFICATION_CATEGORIES_COLLECTION, specCategoryId, false);
        if (data) {
          setFormData({ name: data.name || '', description: data.description || '' });
        }
      } catch (err) {
        setError(translate('specCategory.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [specCategoryId, db, translate]);

  const handleInputChange = (field: keyof BusinessSpecificationCategory, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateForm = (): boolean => {
    const name = formData.name;
    if (typeof name === 'string' && !name.trim()) { setError(translate('specCategory.nameRequired')); return false; }
    if (typeof name === 'object' && !Object.values(name).some(v => v.trim())) { setError(translate('specCategory.nameRequired')); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !db) return;
    setSaving(true);
    setError(null);
    try {
      const result = await saveDocument(firestore, SPECIFICATION_CATEGORIES_COLLECTION, formData, { id: specCategoryId });
      setSuccess(true);
      if (onSave) {
        onSave({ ...formData, id: result.id });
      } else {
        if (!specCategoryId) setFormData({ name: '', description: '' });
        setSuccess(false);
      }
    } catch (err: any) {
      setError(err.message || translate('specCategory.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    else { setFormData({ name: '', description: '' }); setError(null); }
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px"><CircularProgress /></Box>;

  return (
    <Card sx={{ borderRadius: 3, boxShadow: theme.palette.mode === 'dark' ? `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}` : `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}` }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3, color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900] }}>
          {specCategoryId ? translate('specCategory.editTitle') : translate('specCategory.createTitle')}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{translate('specCategory.savedSuccessfully')}</Alert>}

        <Grid container spacing={3}>
          {/* Name */}
          <Grid item xs={12}>
            {usedLocalization?.supported?.length > 1 ? (
              <LocalizedTextField
                languageConfig={usedLocalization}
                value={typeof formData.name === 'object' ? formData.name : { [usedLocalization.preferred]: formData.name || '' }}
                onChange={(value: any) => handleInputChange('name', value)}
                label={translate('specCategory.name')}
                required
                helperText={translate('specCategory.enterName')}
                variant="outlined"
              />
            ) : (
              <TextField
                fullWidth
                label={translate('specCategory.name')}
                value={typeof formData.name === 'string' ? formData.name : (formData.name as any)?.[usedLocalization?.preferred || 'en-US'] || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                helperText={translate('specCategory.enterName')}
                variant="outlined"
              />
            )}
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            {usedLocalization?.supported?.length > 1 ? (
              <LocalizedTextField
                languageConfig={usedLocalization}
                value={typeof formData.description === 'object' ? formData.description : { [usedLocalization.preferred]: (formData.description as string) || '' }}
                onChange={(value: any) => handleInputChange('description', value)}
                label={translate('specCategory.description')}
                multiline
                rows={3}
                helperText={translate('specCategory.enterDescription')}
                variant="outlined"
              />
            ) : (
              <TextField
                fullWidth
                label={translate('specCategory.description')}
                value={typeof formData.description === 'string' ? formData.description : (formData.description as any)?.[usedLocalization?.preferred || 'en-US'] || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                helperText={translate('specCategory.enterDescription')}
                variant="outlined"
              />
            )}
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <ApplicationButton variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} disabled={saving} size="large">
                {translate('common.cancel')}
              </ApplicationButton>
              <ApplicationButton
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                size="large"
                sx={{
                  minWidth: 150,
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`
                    : `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                  boxShadow: `0 3px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': { boxShadow: `0 5px 20px ${alpha(theme.palette.primary.main, 0.4)}` },
                }}
              >
                {saving ? translate('common.loading') : translate('common.save')}
              </ApplicationButton>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default UpsertSpecificationCategory;
